import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Pencil, Plus, Tags } from 'lucide-react';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { Button } from '@/components/ui/button';
import { pcmpRequestCategoriesQueryOptions } from '../../api/pcmpRequestCategories.api';
import { PCMP_REQUEST_APPLIES_TO_LABELS } from '../../constants/pcmpRequest.constants';
import type { PcmpRequestCategory } from '../../types/pcmpRequestCategory.types';
import { PcmpProjectStatusBadge } from '../PcmpProjectStatusBadge';
import { PcmpRequestCategoryDialog } from './PcmpRequestCategoryDialog';

/** Legacy RequestCategories: the admin's list of what a request can ask for. */
export function PcmpRequestCategoriesTable() {
  const { data, isLoading, isFetching, error, refetch } = useQuery(
    pcmpRequestCategoriesQueryOptions(),
  );
  // `undefined` = closed, `null` = adding, a category = editing it.
  const [editing, setEditing] = useState<PcmpRequestCategory | null | undefined>(undefined);

  const columns = useMemo<ColumnDef<PcmpRequestCategory>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Category',
        cell: ({ row }) => (
          <div className="max-w-80 truncate" title={row.original.name}>
            <span className="font-medium">{row.original.name}</span>
            <div className="truncate font-mono text-xs text-muted-foreground">
              {row.original.code}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'appliesTo',
        header: 'Raised from',
        cell: ({ row }) => PCMP_REQUEST_APPLIES_TO_LABELS[row.original.appliesTo],
      },
      {
        accessorKey: 'actionPlanLabel',
        header: 'Approving does',
        cell: ({ row }) =>
          row.original.actionPlanKey ? (
            row.original.actionPlanLabel
          ) : (
            <span className="text-muted-foreground">Records the decision only</span>
          ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => <PcmpProjectStatusBadge isActive={row.original.isActive} />,
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => setEditing(row.original)}
          >
            <Pencil className="size-3.5" />
            Edit
          </Button>
        ),
      },
    ],
    [],
  );

  return (
    <div className="flex flex-1 flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold">Request Categories</h1>
          <p className="text-sm text-muted-foreground">
            What a priority request can ask for, and what approving it does.
          </p>
        </div>
        <Button type="button" className="gap-1.5" onClick={() => setEditing(null)}>
          <Plus className="size-4" />
          Add Category
        </Button>
      </div>

      {error ? (
        <ErrorState
          error={error}
          fallbackTitle="Could not load the request categories"
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          className="min-h-0 flex-1"
        />
      ) : (
        <DataTable
          data={data ?? []}
          columns={columns}
          isLoading={isLoading}
          emptyIcon={Tags}
          emptyMessage="No request categories yet"
          getRowClassName={(category) => (category.isActive ? undefined : 'opacity-60')}
          className="flex-1 min-h-0"
        />
      )}

      {editing !== undefined ? (
        <PcmpRequestCategoryDialog category={editing} onClose={() => setEditing(undefined)} />
      ) : null}
    </div>
  );
}
